import { useEffect, useRef, type RefObject } from 'react'

/**
 * El contrato de foco de un diálogo, en un solo lugar.
 *
 * `Modal` y `Drawer` declaran `aria-modal="true"`, y eso es una promesa: el
 * foco entra al diálogo, no se escapa mientras está abierto, y **vuelve al
 * elemento que lo abrió** al cerrarse. Sin lo último, cerrar con Escape en una
 * tabla de 31 filas devuelve al usuario de teclado al principio del documento y
 * tiene que tabular de nuevo hasta donde estaba.
 *
 * El trap se hace ciclando Tab adentro del diálogo en vez de marcar el fondo
 * como `inert`: el fondo lo monta el shell, que no es de este componente, y un
 * ciclo de Tab da la misma garantía para el teclado sin tocarlo.
 *
 * Los diálogos se **apilan**: la ficha de un cliente es un `Drawer` y el botón
 * de crear un seguimiento abre un `Modal` encima, con el Drawer todavía montado.
 * Sin disciplina de pila los dos traps se pelean —el de abajo tira el foco a su
 * panel y el de arriba lo devuelve al suyo, y cada Tab reinicia el ciclo en el
 * botón Cerrar— y un Escape cierra los dos de una. Por eso hay una pila a nivel
 * de módulo y **solo el diálogo de más arriba maneja el teclado**.
 */
export interface DialogFocus<C extends HTMLElement, B extends HTMLElement> {
  /** Va en el nodo con `role="dialog"`: delimita el ciclo de Tab. */
  contenedorRef: RefObject<C>
  /** Va en el botón de cerrar: es lo primero que recibe el foco al abrir. */
  cerrarRef: RefObject<B>
}

const ENFOCABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Los contenedores de los diálogos abiertos, en orden de apertura. El último es
 * el que está arriba de todo y el único que atiende Tab y Escape.
 *
 * Es estado de módulo a propósito: los diálogos que se apilan no son parientes
 * en el árbol (el `Modal` se monta como hermano del `Drawer`, no adentro), así
 * que no hay contexto de React que los una sin obligar a cada pantalla a montar
 * un provider.
 */
const pila: Array<RefObject<HTMLElement>> = []

function enfocablesDe(contenedor: HTMLElement): HTMLElement[] {
  return Array.from(
    contenedor.querySelectorAll<HTMLElement>(ENFOCABLES),
  ).filter(
    (nodo) => nodo.getAttribute('aria-hidden') !== 'true' && !nodo.hidden,
  )
}

export function useDialogFocus<
  C extends HTMLElement,
  B extends HTMLElement,
>(open: boolean, onClose: () => void): DialogFocus<C, B> {
  const contenedorRef = useRef<C>(null)
  const cerrarRef = useRef<B>(null)

  // `onClose` suele venir como flecha inline, así que cambia de identidad en
  // cada render. Si estuviera en las dependencias del efecto, el cleanup
  // correría en cada render y le devolvería el foco al disparador con el
  // diálogo todavía abierto.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return

    const previo =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    cerrarRef.current?.focus()

    pila.push(contenedorRef)

    /** Solo el de más arriba manda: los de abajo quedan en silencio. */
    function esElDeArriba(): boolean {
      return pila[pila.length - 1] === contenedorRef
    }

    function onKeyDown(evento: globalThis.KeyboardEvent) {
      if (!esElDeArriba()) return

      if (evento.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (evento.key !== 'Tab') return

      const contenedor = contenedorRef.current
      if (contenedor === null) return

      const foco = enfocablesDe(contenedor)
      const primero = foco[0]
      const ultimo = foco[foco.length - 1]
      if (primero === undefined || ultimo === undefined) {
        // Un diálogo sin nada enfocable adentro no puede dejar salir el foco.
        evento.preventDefault()
        return
      }

      const activo = document.activeElement
      const adentro = activo !== null && contenedor.contains(activo)

      if (evento.shiftKey) {
        if (!adentro || activo === primero) {
          evento.preventDefault()
          ultimo.focus()
        }
        return
      }
      if (!adentro || activo === ultimo) {
        evento.preventDefault()
        primero.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Por identidad y no `pop()`: el de abajo puede cerrarse primero (la ficha
      // se desmonta y deja el modal de confirmación arriba), y sacar el último
      // dejaría mudo al que sigue en pantalla.
      const eraElDeArriba = esElDeArriba()
      const donde = pila.indexOf(contenedorRef)
      if (donde !== -1) pila.splice(donde, 1)
      // Devolver el foco es del que estaba arriba: si este diálogo se cerró con
      // otro encima, tirar el foco a su disparador se lo sacaría al que sigue
      // abierto.
      if (!eraElDeArriba) return
      // Solo si el disparador sigue en el documento: si el cierre lo desmontó
      // (una fila que se fue de la tabla), forzarlo tiraría un error y el foco
      // queda donde el navegador lo deje.
      if (previo !== null && previo.isConnected) previo.focus()
    }
  }, [open])

  return { contenedorRef, cerrarRef }
}
